import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount, type Component, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { Input } from '~ui/input';
import { SearchIcon } from '~ui/icons';
import { formatPercent, todayISO } from '~/lib/format';
import { COVERED, instrumentYield, mandateOf, name } from '~data';
import { screen } from '~/stores/explore-store';
import HeldMark from '~/features/held-mark';

// Picking one fund out of the pool.
//
// A row of every symbol was fine at eighteen and is not a design — it is a list
// that happens to fit. At forty it wraps or scrolls past the edge, at a hundred
// it is unusable, and the whole time it costs the width of the toolbar to say
// something the trigger below says in one line.
//
// Search matching is `screen` from explore-store, the same function behind the
// screener's search bar: symbol, name, mandate and issuer, with multiple terms
// matched independently so "hamilton util" finds UMAX. One implementation, so
// the two places cannot disagree about what a query means.
//
// The menu is portalled and positioned `fixed` from the trigger's rect, because
// Panel is overflow-hidden and a menu absolutely positioned inside the toolbar
// would be clipped to the toolbar's own height. Same approach as the shared
// Select, which is app-local here rather than a change to that component: it is
// consumed by every app and this needs a search field and a two-part trigger
// that nothing else asks for.

const ASOF = todayISO();

/** Space to leave under the menu so it never runs into the window edge. */
const MARGIN = 8;

export const FundPicker: Component<{ symbol: string; onSelect: (symbol: string) => void; placeholder?: string; class?: string; icon?: JSX.Element; omit?: string[] }> = (props) => {
    const [open, setOpen] = createSignal(false);
    const [query, setQuery] = createSignal('');
    const [active, setActive] = createSignal(0);
    const [box, setBox] = createSignal({ top: 0, left: 0, width: 0, maxHeight: 320 });

    let trigger: HTMLButtonElement | undefined;
    let menu: HTMLDivElement | undefined;

    // Anything already chosen is dropped from the list. On a multi-select the
    // control is "add one more", and offering something that is already in is
    // offering a click that does nothing.
    const matches = createMemo(() => {
        const omit = new Set(props.omit ?? []);
        return screen(COVERED, query(), null).filter((f) => !omit.has(f.symbol));
    });

    const place = (): void => {
        if (!trigger) return;
        const r = trigger.getBoundingClientRect();
        setBox({ top: r.bottom, left: r.left, width: r.width, maxHeight: Math.max(120, window.innerHeight - r.bottom - MARGIN) });
    };

    const show = (): void => {
        setQuery('');
        setActive(0);
        place();
        setOpen(true);
    };

    const choose = (symbol: string): void => {
        props.onSelect(symbol);
        setOpen(false);
    };

    // The highlight has to stay inside the list as it shrinks under typing —
    // otherwise Enter after a narrowing keystroke selects nothing, or worse,
    // whatever used to be at that index.
    createEffect(() => {
        const count = matches().length;
        if (active() >= count) setActive(Math.max(0, count - 1));
    });

    const keys = (e: KeyboardEvent): void => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, matches().length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const hit = matches()[active()];
            if (hit) choose(hit.symbol);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            trigger?.focus();
        }
    };

    // The menu is not a DOM descendant of the trigger, so containment has to be
    // tested against both.
    const outside = (e: MouseEvent): void => {
        const target = e.target as Node;
        if (trigger?.contains(target) || menu?.contains(target)) return;
        setOpen(false);
    };

    const reposition = (): void => {
        if (open()) place();
    };

    onMount(() => {
        document.addEventListener('click', outside);
        // Capture, so a scroll on an inner container moves the menu too.
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
    });

    onCleanup(() => {
        document.removeEventListener('click', outside);
        window.removeEventListener('scroll', reposition, true);
        window.removeEventListener('resize', reposition);
    });

    return (
        <>
            <button
                ref={trigger}
                type='button'
                title='Choose a fund'
                onClick={(e) => {
                    e.stopPropagation();
                    open() ? setOpen(false) : show();
                }}
                class={`flex h-full min-w-0 max-w-96 flex-1 cursor-default items-center gap-2 rounded px-1.5 ${open() ? 'bg-muted-foreground/20' : 'hover:bg-muted'} ${props.class ?? ''}`}>
                <span class='shrink-0 text-muted-foreground'>{props.icon ?? <SearchIcon />}</span>
                {/* With no fund chosen the trigger is an invitation rather than
                    a statement — the Sandbox uses it to add a position, where
                    "Fund" on its own would read as a broken label. */}
                <Show
                    when={props.symbol}
                    fallback={
                        <Text tone='muted' class='min-w-0 truncate'>
                            {props.placeholder ?? 'Fund'}
                        </Text>
                    }>
                    <Text weight='medium' class='shrink-0'>
                        {props.symbol}
                    </Text>
                    <Text tone='muted' class='min-w-0 truncate'>
                        {name(props.symbol) ?? ''}
                    </Text>
                </Show>
            </button>

            <Show when={open()}>
                <Portal>
                    <div
                        ref={menu}
                        class='fixed z-[100] text-foreground flex min-w-72 flex-col overflow-hidden rounded border bg-background shadow'
                        style={{ top: `${box().top}px`, left: `${box().left}px`, 'min-width': `${Math.max(box().width, 288)}px`, 'max-height': `${box().maxHeight}px` }}
                        onClick={(e) => e.stopPropagation()}>
                        {/* Focus is taken by querying the wrapper rather than by
                            passing a ref through Input's prop spread. Solid does
                            forward a ref that way, but this control's usefulness
                            rests entirely on being able to type the moment it
                            opens, and that is not worth making contingent on
                            another component's prop plumbing. */}
                        <div
                            class='flex h-row shrink-0 items-center gap-1 border-b px-2'
                            ref={(el: HTMLDivElement) => queueMicrotask(() => el.querySelector('input')?.focus())}>
                            <span class='shrink-0 text-muted-foreground'>
                                <SearchIcon />
                            </span>
                            <Input
                                bare
                                value={query()}
                                placeholder='Search funds'
                                class='min-w-0 py-0!'
                                onInput={(e) => {
                                    setQuery(e.currentTarget.value);
                                    setActive(0);
                                }}
                                onKeyDown={keys}
                            />
                        </div>

                        <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
                            <Show when={matches().length > 0} fallback={<Text tone='muted' class='block px-2 py-3 text-center'>No fund matches</Text>}>
                                <For each={matches()}>
                                    {(f, i) => (
                                        <button
                                            type='button'
                                            // Hover moves the highlight, so the
                                            // pointer and the keyboard never
                                            // disagree about what Enter will take.
                                            onMouseEnter={() => setActive(i())}
                                            onClick={() => choose(f.symbol)}
                                            class={`flex h-row w-full cursor-default items-center gap-2 px-2 text-left text-sm ${i() === active() ? 'bg-muted' : ''} ${f.symbol === props.symbol ? 'bg-muted-foreground/20' : ''}`}>
                                            {/* Fixed-width so the marked rows do
                                                not shunt the names out of line —
                                                a column of names that steps in
                                                and out is harder to scan than the
                                                mark is worth. */}
                                            <span class='flex w-14 shrink-0 items-center gap-1'>
                                                <Text weight='medium'>{f.symbol}</Text>
                                                <HeldMark symbol={f.symbol} />
                                            </span>
                                            <Text tone='muted' class='min-w-0 flex-1 truncate'>
                                                {name(f.symbol) ?? mandateOf(f.symbol) ?? ''}
                                            </Text>
                                            <Text class='shrink-0'>{formatPercent(instrumentYield(f.symbol, ASOF))}</Text>
                                        </button>
                                    )}
                                </For>
                            </Show>
                        </div>
                    </div>
                </Portal>
            </Show>
        </>
    );
};

export default FundPicker;
